/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _objectSpread from "@babel/runtime-corejs3/helpers/esm/objectSpread2";
import _concatInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/concat";
import _toConsumableArray from "@babel/runtime-corejs3/helpers/esm/toConsumableArray";
import _sliceInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/slice";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _Date$now from "@babel/runtime-corejs3/core-js-stable/date/now";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime-corejs3/helpers/esm/assertThisInitialized";
import _getPrototypeOf from "@babel/runtime-corejs3/helpers/esm/getPrototypeOf";
import _get from "@babel/runtime-corejs3/helpers/esm/get";
import _inherits from "@babel/runtime-corejs3/helpers/esm/inherits";
import _createSuper from "@babel/runtime-corejs3/helpers/esm/createSuper";

/** @module browser/classes/ajax-request */
import { log, randomizer } from '@codistica/core';
import { EventEmitter } from 'eventemitter3'; // TODO: LOG AND PASS ERRORS
// TODO: REMOVE EVENT LISTENERS WHEN FINISHED
// TODO: REDUCE SIZE IF POSSIBLE

/**
 * @typedef ajaxRequestOptionsType
 * @property {string} path - Request URL.
 * @property {string} [id] - Request ID.
 * @property {string} [requestMethod='GET'] - HTTP method to be used.
 * @property {(''|'arraybuffer'|'blob'|'document'|'json'|'text')} [responseType='text'] - Response type.
 * @property {boolean} [noCache=false] - If true, appends unique queries to the request URL so fresh responses are forced.
 */

/**
 * @classdesc XMLHttpRequest wrapper with eventemitter3 support.
 */

var AJAXRequest = /*#__PURE__*/function (_EventEmitter) {
  _inherits(AJAXRequest, _EventEmitter);

  var _super = _createSuper(AJAXRequest);

  /**
   * @description Constructor.
   * @param {ajaxRequestOptionsType} options - AJAXRequest options.
   */
  function AJAXRequest(options) {
    var _context,
        _context2,
        _context3,
        _context4,
        _this2 = this,
        _context5,
        _context6,
        _context7,
        _context8,
        _context9,
        _context10,
        _context11;

    var _this;

    _classCallCheck(this, AJAXRequest);

    _this = _super.call(this);
    _this.path = options.path;
    _this.id = typeof options.id === 'string' ? options.id : randomizer.getUniqueId();
    _this.options = {
      requestMethod: typeof options.requestMethod === 'string' ? options.requestMethod : 'GET',
      responseType: typeof options.responseType === 'string' ? options.responseType : 'text',
      noCache: typeof options.noCache === 'boolean' ? options.noCache : false
    };
    _this.stats = {
      sendTimestamp: null
    }; // BIND METHODS

    _this.send = _bindInstanceProperty(_context = _this.send).call(_context, _assertThisInitialized(_this));
    _this.abort = _bindInstanceProperty(_context2 = _this.abort).call(_context2, _assertThisInitialized(_this));
    _this.fail = _bindInstanceProperty(_context3 = _this.fail).call(_context3, _assertThisInitialized(_this));
    _this.emit = _bindInstanceProperty(_context4 = _this.emit).call(_context4, _assertThisInitialized(_this)); // SAVE REQUEST URL
    // TODO: IMPROVE QUERY APPENDING. (DO NOT DESTROY EXISTING)

    _this.requestUrl = _this.path + (_this.options.noCache ? (/\?/.test(_this.path) ? '&' : '?') + _Date$now() : ''); // CREATE REQUEST

    _this.request = new XMLHttpRequest(); // SETUP REQUEST

    _this.request.responseType = _this.options.responseType; // TRACK STATUS

    _this.request.addEventListener('readystatechange', _bindInstanceProperty(_context5 = function _context5() {
      _newArrowCheck(this, _this2);

      if (_this.request.status >= 400 && _this.request.status <= 599) {
        // CASE: HTTP ERROR OCCURRED
        log.error('AJAXRequest()', "".concat(_this.requestUrl, " - HTTP ERROR"))(); // FAIL

        _this.fail();
      } else if (_this.request.readyState >= 2 && _this.request.status <= 99) {
        // MUST BE NESTED
        if (_this.request.status === null) {
          // CASE: POSSIBLE REQUEST ERROR OCCURRED
          log.warning('AJAXRequest()', "".concat(_this.requestUrl, " - REQUEST ERROR CAN BE EXPECTED"))();
        }
      } else if (_this.request.readyState === 2) {
        // CASE: HEADERS RECEIVED
        _this.emit('headers');
      } else if (_this.request.readyState === 4) {
        // CASE: DOWNLOAD SUCCESSFULLY COMPLETED
        _this.emit('success');
      }
    }).call(_context5, this)); // TRACK PROGRESS


    _this.request.addEventListener('progress', _bindInstanceProperty(_context6 = function _context6(e) {
      _newArrowCheck(this, _this2);

      if (_this.request.status >= 100 && _this.request.status <= 399 && e.loaded > 0) {
        _this.emit('progress', {
          loaded: e.loaded,
          total: e.total,
          lengthComputable: e.lengthComputable
        });
      }
    }).call(_context6, this)); // LOAD START


    _this.request.addEventListener('loadstart', _bindInstanceProperty(_context7 = function _context7() {
      _newArrowCheck(this, _this2);

      // CASE: START
      _this.emit('start');
    }).call(_context7, this)); // LOAD END


    _this.request.addEventListener('loadend', _bindInstanceProperty(_context8 = function _context8() {
      _newArrowCheck(this, _this2);

      // CASE: END
      _this.emit('end');
    }).call(_context8, this)); // ERROR HANDLER


    _this.request.addEventListener('error', _bindInstanceProperty(_context9 = function _context9() {
      _newArrowCheck(this, _this2);

      // CASE: REQUEST ERROR OCCURRED
      log.error('AJAXRequest()', "".concat(_this.requestUrl, " - REQUEST ERROR"))();

      _this.emit('error'); // FAIL


      _this.fail();
    }).call(_context9, this)); // TIMEOUT HANDLER


    _this.request.addEventListener('timeout', _bindInstanceProperty(_context10 = function _context10() {
      _newArrowCheck(this, _this2);

      // CASE: TIMEOUT
      log.warning('AJAXRequest()', "".concat(_this.requestUrl, " - REQUEST HAS TIMED OUT"))();

      _this.emit('timeout'); // FAIL


      _this.fail();
    }).call(_context10, this)); // ABORT HANDLER


    _this.request.addEventListener('abort', _bindInstanceProperty(_context11 = function _context11() {
      _newArrowCheck(this, _this2);

      // CASE: ABORTED
      log.info('AJAXRequest()', "".concat(_this.requestUrl, " - REQUEST ABORTED"))();

      _this.emit('abort');
    }).call(_context11, this));

    return _this;
  }

  _createClass(AJAXRequest, [{
    key: "send",
    value: function send() {
      log.debug('AJAXRequest()', "".concat(this.requestUrl, " - SENDING REQUEST"))();
      this.emit('send');
      this.request.open(this.options.requestMethod, this.requestUrl);
      this.request.send();
      this.stats.sendTimestamp = _Date$now();
    }
  }, {
    key: "fail",
    value: function fail() {
      log.error('AJAXRequest()', "".concat(this.requestUrl, " - REQUEST FAILED"))();
      this.emit('fail'); // ABORT REQUEST

      this.abort();
    }
  }, {
    key: "abort",
    value: function abort() {
      log.info('AJAXRequest()', "".concat(this.requestUrl, " - REQUESTING ABORTION"))();

      if (typeof this.request.abort === 'function') {
        this.request.abort();
      } else {
        log.warning('AJAXRequest()', "".concat(this.requestUrl, " - CANNOT BE ABORTED"))();
      }
    }
    /**
     * @description Event emission auxiliary method.
     * @param {(string|symbol)} event - Event name to be emitted.
     * @param {...*} args - Arguments to be passed to handlers.
     * @returns {boolean} Event emission result.
     */

  }, {
    key: "emit",
    value: function emit(event) {
      var _get2, _context12;

      var now = _Date$now();

      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      var extraProps = args[0],
          otherArgs = _sliceInstanceProperty(args).call(args, 1);

      return (_get2 = _get(_getPrototypeOf(AJAXRequest.prototype), "emit", this)).call.apply(_get2, _concatInstanceProperty(_context12 = [this, event, _objectSpread({
        that: this,
        now: now
      }, extraProps || {})]).call(_context12, _toConsumableArray(otherArgs || [])));
    }
  }]);

  return AJAXRequest;
}(EventEmitter);

export { AJAXRequest };